import { Grid } from '@mui/material';
import { FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

import { FormPart } from '@modules/shared/components/FormPart';
import { SERVICES } from '@shared/constants/apiConfig';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { TextInputField } from '@shared/fields/TextInputField';
import { useIsFirstRender } from '@shared/hooks/useIsFirstRender';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { CommonButtonWrapper, PageContent } from '@styles/CommonStyles';

import { IConfiguration, IGroupsTokens } from '../types';

interface IConfigurationTableProps extends Partial<FormikProps<IConfiguration>> {
  handleClose: () => void;
}

export const ConfigurationTableForm = ({ setFieldValue, handleClose, values }:IConfigurationTableProps) => {
  const isFirstRender = useIsFirstRender();

  const [includedTokenNames, setIncludedTokenNames] = useState<string[] | null>(null);
  const [excludedTokenNames, setExcludedTokenNames] = useState<string[] | null>(null);

  const { data: configurationTables } = useSWR<IConfiguration[]>(`${SERVICES.CONFIGURATION}/configurations/all`, {
    revalidateOnMount: undefined,
  });
  const { data: groupsTokens } = useSWR<IGroupsTokens>(`${SERVICES.CONFIGURATION}/configurations/groups-tokens`, {
    revalidateOnMount: undefined,
  });

  useEffect(() => {
    if (groupsTokens && values?.configurationGroup && configurationTables) {
      setIncludedTokenNames(
        configurationTables
          ?.filter((item) => item.configurationGroup === values.configurationGroup)
          .map((i) => i.tokenName),
      );
    }
  }, [configurationTables, groupsTokens, values?.configurationGroup]);

  useEffect(() => {
    if (groupsTokens && includedTokenNames) {
      setExcludedTokenNames(groupsTokens.tokenNames.filter((i) => !includedTokenNames.includes(i)).sort());
    }
  }, [includedTokenNames]);

  useEffect(() => {
    !isFirstRender && setFieldValue?.('tokenName', '');
  }, [values?.configurationGroup]);

  const formConfig = [
    {
      width: '50%',
      name: 'configurationGroup',
      isCreatable: true,
      key: groupsTokens?.configurationGroups.length,
      options: groupsTokens?.configurationGroups
      ? [...groupsTokens.configurationGroups].sort().map((group) => ({ value: group, label: group }))
      : [],
      onChange: (value: string) => setFieldValue?.('configurationGroup', value),
      shouldUpdate: () => true,
      component: SelectAutocompleteField,
    },
    {
      width: '50%',
      name: 'tokenName',
      isCreatable: true,
      key: excludedTokenNames?.length,
      options: excludedTokenNames?.map((group) => ({ value: group, label: group })) ?? [],
      onChange: (value: string) => setFieldValue?.('tokenName', value),
      shouldUpdate: () => true,
      component: SelectAutocompleteField,
    },
    {
      width: '100%',
      name: 'value',
      multiline: true,
      maxRows: 5,
      component: TextInputField,
    },
    {
      width: '100%',
      name: 'description',
      label: 'Description/Rules',
      multiline: true,
      maxRows: 5,
      component: TextInputField,
    },
  ];

  return (
    <>
      <PageContent>
        <Grid container spacing={12}>
          <Grid item md={12}>
            <FormPart config={formConfig} />
          </Grid>
        </Grid>
      </PageContent>
      
      <CommonButtonWrapper direction="row">
        <CancelButton onClick={handleClose} />
        <SaveButton />
      </CommonButtonWrapper>
    </>
  );
};
